/* Shared navigation types + fallback menu (plain data, server/client safe). */

import type { AiSettings } from './ai-summarize';

export type NavLink = {
  label: string;
  url: string;
  icon?: string | null;
  description?: string | null;
  openInNewTab?: boolean | null;
};

export type NavColumn = {
  heading?: string | null;
  links?: NavLink[] | null;
};

export type NavFeature = {
  enabled?: boolean | null;
  title?: string | null;
  text?: string | null;
  buttonLabel?: string | null;
  buttonUrl?: string | null;
};

export type NavItem = {
  label: string;
  icon?: string | null;
  type: 'link' | 'dropdown' | 'mega';
  url?: string | null;
  openInNewTab?: boolean | null;
  columns?: NavColumn[] | null;
  feature?: NavFeature | null;
};

export type CtaButton = {
  label: string;
  url: string;
  style: 'outline' | 'primary';
  openInNewTab?: boolean | null;
};

export type FooterLink = {
  label: string;
  url: string;
  openInNewTab?: boolean | null;
};

export type FooterColumn = {
  heading?: string | null;
  links?: FooterLink[] | null;
};

export type SocialSettings = {
  facebook?: string;
  twitter?: string;
  linkedin?: string;
  youtube?: string;
  instagram?: string;
};

export type NavData = {
  headerItems: NavItem[];
  ctaButtons: CtaButton[];
  footerColumns: FooterColumn[];
  social?: SocialSettings;
  /** Resolved Site Settings `ai` group; undefined when site-settings failed to load (footer renders nothing AI-related). */
  ai?: AiSettings;
};

/* Used until the Navigation global is populated.
 *
 * URLs MUST match the live Navigation global (they were re-synced from prod on
 * 10 Aug 2026). They had drifted badly: every product link still pointed at the
 * old FLAT slug (/hr-software, /time-tracking-software, …) and every industry
 * link at a short slug (/industries/manufacturing, …). Those flat pages are now
 * unpublished drafts that 301, and the short industry slugs were served by a
 * hardcoded "coming soon" route that has since been deleted — so this fallback
 * pointed at 25 URLs that no longer resolve. It only surfaces when the global is
 * empty (fresh DB / local dev), which is exactly when nobody notices.
 * tests/unit/cms-only-routes.test.ts guards the shape. */
export const fallbackHeaderItems: NavItem[] = [
  {
    label: 'Products',
    type: 'mega',
    columns: [
      {
        heading: 'HR Management',
        links: [
          { label: 'HR Management Software', url: '/products/hr-management-software', icon: 'Users', description: 'Centralized employee records & documents' },
          { label: 'Leave Management System', url: '/products/leave-management-system', icon: 'CalendarDays', description: 'PTO policies, accruals & absence tracking' },
          { label: 'Attendance Tracking Software', url: '/products/attendance-tracking-software', icon: 'Clock', description: 'Web check-in, biometric & automated capture' },
          { label: 'Performance Management System', url: '/products/employee-performance-mangement-system', icon: 'TrendingUp', description: 'Goals, reviews & assessment cycles' },
        ],
      },
      {
        heading: 'Workforce',
        links: [
          { label: 'Employee Monitoring Software', url: '/products/employee-monitoring-software', icon: 'Eye', description: 'Activity tracking & productivity insights' },
          { label: 'Shift Scheduling Software', url: '/products/employee-shift-scheduling-software', icon: 'CalendarClock', description: 'Shift scheduling, reminders & penalties' },
          { label: 'Location Tracking Software', url: '/products/employee-location-tracking-software', icon: 'MapPin', description: 'Real-time employee location visibility' },
          { label: 'Call Tracking Software', url: '/products/call-tracking-software', icon: 'Phone', description: 'Call insights, tracking & reporting' },
        ],
      },
      {
        heading: 'Operations',
        links: [
          { label: 'Payroll Software', url: '/products/cloud-based-payroll-software', icon: 'Wallet', description: 'Automated payroll, expenses & payslips' },
          { label: 'Task Management Software', url: '/products/task-management-software', icon: 'CheckSquare', description: 'Projects, task boards & workflows' },
          { label: 'Communication & Collaboration', url: '/products/team-communication-collaboration-software', icon: 'MessagesSquare', description: 'Internal social feed & announcements' },
          { label: 'Time Tracking Software', url: '/products/employee-timesheet-software', icon: 'Timer', description: 'Timesheets, project hours & approvals' },
        ],
      },
    ],
    feature: {
      enabled: true,
      title: 'All-in-one HRMS for modern teams',
      text: 'Manage HR, attendance, payroll and field operations from a single cloud platform.',
      buttonLabel: 'Book a Demo',
      buttonUrl: '/schedule-a-demo',
    },
  },
  {
    label: 'Industries',
    type: 'mega',
    columns: [
      {
        heading: 'Primary Industries',
        links: [
          { label: 'Manufacturing', url: '/industries/hr-software-for-manufacturing', icon: 'Factory' },
          { label: 'SaaS / IT', url: '/industries/saas-hr-software', icon: 'Cloud' },
          { label: 'Healthcare', url: '/industries/hr-software-for-healthcare', icon: 'HeartPulse' },
          { label: 'Pharma', url: '/industries/hr-software-for-pharma', icon: 'Pill' },
        ],
      },
      {
        heading: 'Business & Retail',
        links: [
          { label: 'Retail', url: '/industries/hr-software-for-retail', icon: 'ShoppingCart' },
          { label: 'Education', url: '/industries/hr-software-for-education', icon: 'GraduationCap' },
          { label: 'Finance', url: '/industries/hr-software-for-finance', icon: 'Landmark' },
          { label: 'Startups', url: '/industries/hr-software-for-startups', icon: 'Rocket' },
        ],
      },
      {
        heading: 'Operations',
        links: [
          { label: 'Logistics', url: '/industries/logistics-hr-software', icon: 'Truck' },
          { label: 'Automotive', url: '/industries/hrms-software-for-automobile', icon: 'Car' },
          { label: 'Hospitality', url: '/industries/hr-software-for-hospitality', icon: 'Hotel' },
        ],
      },
      {
        heading: 'Technology & Welfare',
        links: [
          { label: 'Information Technology', url: '/industries/hr-software-for-information-technology', icon: 'Laptop' },
          { label: 'Social Welfare', url: '/industries/hr-software-for-ngo', icon: 'HandHeart' },
          { label: 'E-commerce', url: '/industries/ecommerce-hr-software', icon: 'Rocket' },
        ],
      },
    ],
  },
  { label: 'Pricing', type: 'link', url: '/pricing' },
  {
    label: 'Resources',
    type: 'dropdown',
    columns: [
      {
        heading: 'Resources',
        links: [
          { label: 'Blog', url: '/blog', icon: 'PenLine', description: 'HR tips, guides & industry insights' },
          { label: 'Reviews', url: '/reviews', icon: 'Star', description: 'What our customers say about us' },
          { label: 'Case Studies', url: '/case-studies', icon: 'LayoutGrid', description: 'Real results from real teams' },
          { label: 'Glossary', url: '/glossary', icon: 'BookA', description: 'HR & payroll terms explained' },
          { label: 'Help', url: '/help', icon: 'CircleQuestionMark', description: 'Guides & product documentation' },
          { label: "What's New", url: '/whats-new-in-officeportal', icon: 'Sparkles', description: 'Latest product updates & features' },
        ],
      },
    ],
  },
  { label: 'Contact Us', type: 'link', url: '/contact-us' },
];

export const fallbackCtaButtons: CtaButton[] = [
  { label: 'Book a Demo', url: '/schedule-a-demo', style: 'outline' },
  { label: 'Start Free', url: '/setup-free-account', style: 'primary' },
];

export const fallbackFooterColumns: FooterColumn[] = [
  {
    heading: 'Product',
    links: [
      { label: 'Attendance Tracking', url: '/products/attendance-tracking-software' },
      { label: 'Leave Management', url: '/products/leave-management-system' },
      { label: 'Payroll', url: '/products/cloud-based-payroll-software' },
      { label: 'Shift & Rota', url: '/products/employee-shift-scheduling-software' },
      { label: 'Performance', url: '/products/employee-performance-mangement-system' },
      { label: 'Employee Monitoring', url: '/products/employee-monitoring-software' },
      { label: 'Location Tracker', url: '/products/employee-location-tracking-software' },
      { label: 'Call Tracker', url: '/products/call-tracking-software' },
      { label: 'Time Tracking', url: '/products/employee-timesheet-software' },
      { label: 'Collaboration', url: '/products/team-communication-collaboration-software' },
    ],
  },
  {
    heading: 'Support',
    links: [
      { label: 'Help Center', url: '/help' },
      { label: "What's New", url: '/whats-new-in-officeportal' },
      { label: 'Blog', url: '/blog' },
      { label: 'Reviews', url: '/reviews' },
      { label: 'Careers', url: '/careers' },
    ],
  },
  {
    heading: 'Get Started',
    links: [
      { label: 'Setup Free Account', url: '/setup-free-account' },
      { label: 'Schedule a Demo', url: '/schedule-a-demo' },
      { label: 'Pricing', url: '/pricing' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'GDPR', url: '/gdpr' },
      { label: 'Privacy Policy', url: '/privacy-policy' },
      { label: 'Cookie Policy', url: '/cookie-policy' },
    ],
  },
];
